/** Обзор: показатели и посещаемость последних мероприятий. */

import type { ReactNode } from 'react';
import type { EventStatus, StatsSummary } from '@ak/shared';

import { formatCount, formatDateTime, formatRelative } from '../lib/format.ts';
import { useLinkHandler } from '../lib/router.ts';
import { useResource } from '../lib/useResource.ts';
import { IconApplications, IconOverview, IconPlayers } from '../components/icons.tsx';
import { Badge, ErrorState, PageHeader, Panel, PanelHeader, Skeleton, StatCard } from '../components/ui.tsx';

interface RecentEvent {
  id: number;
  eventType: string;
  status: EventStatus;
  startsAt: string | null;
  capacity: number;
  plusCount: number;
  reserveCount: number;
}

interface SummaryResponse {
  summary: StatsSummary;
  recentEvents: RecentEvent[];
}

const STATUS_LABEL: Record<EventStatus, string> = {
  open: 'Открыт',
  closed: 'Закрыт',
  cancelled: 'Отменён',
};

const STATUS_TONE: Record<EventStatus, 'positive' | 'neutral' | 'danger'> = {
  open: 'positive',
  closed: 'neutral',
  cancelled: 'danger',
};

/**
 * Доля побед.
 *
 * Считается только по состоявшимся мероприятиям с отмеченным итогом:
 * наборы без отметки в знаменатель не входят, иначе показатель падал бы
 * оттого, что итог забыли проставить. Пока итогов нет, показывать нечего —
 * ноль процентов выглядел бы как поражения.
 */
function WinrateCard({ wins, losses }: { wins: number; losses: number }): ReactNode {
  const played = wins + losses;
  const share = played === 0 ? null : Math.round((wins / played) * 100);
  const tone = share === null ? 'neutral' : share >= 50 ? 'positive' : 'danger';

  return (
    <StatCard
      label="Winrate наборов"
      value={share === null ? '—' : `${String(share)}%`}
      hint={
        played === 0
          ? 'Итоги мероприятий ещё не отмечены'
          : `${formatCount(wins, 'победа', 'победы', 'побед')} и ${formatCount(losses, 'поражение', 'поражения', 'поражений')} из ${formatCount(played, 'мероприятия', 'мероприятий', 'мероприятий')}`
      }
      icon={<IconOverview size={20} />}
      tone={tone}
    />
  );
}

/**
 * Посещаемость столбиками.
 *
 * Рисуется вручную: библиотека графиков ради одной диаграммы весит больше,
 * чем всё остальное приложение, и хуже подчиняется теме.
 */
function AttendanceChart({ events, timezone }: { events: RecentEvent[]; timezone: string }): ReactNode {
  const data = events
    .filter((event) => event.status !== 'cancelled' && event.startsAt !== null)
    .slice(0, 14)
    .reverse();

  if (data.length === 0) {
    return (
      <p className="px-6 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
        Пока нет состоявшихся мероприятий.
      </p>
    );
  }

  const max = Math.max(...data.map((event) => Math.max(event.plusCount + event.reserveCount, event.capacity)), 1);
  const width = 100 / data.length;

  return (
    <div className="px-5 pt-2 pb-5">
      <svg viewBox="0 0 100 42" className="h-48 w-full" role="img" aria-label="Посещаемость последних мероприятий">
        <defs>
          {/* Градиент по высоте столбика: снизу насыщенно, кверху легче. */}
          <linearGradient id="ak-bar" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="var(--accent)" />
            <stop offset="100%" stopColor="var(--accent-far)" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map((ratio) => (
          <line
            key={ratio}
            x1="0"
            x2="100"
            y1={36 - 36 * ratio}
            y2={36 - 36 * ratio}
            stroke="var(--border-subtle)"
            strokeWidth="0.2"
          />
        ))}

        {data.map((event, index) => {
          const barWidth = width * 0.52;
          const gap = (width - barWidth) / 2;
          const x = index * width + gap;
          const mainHeight = (event.plusCount / max) * 36;
          const reserveHeight = (event.reserveCount / max) * 36;

          return (
            <g key={event.id}>
              <title>
                {`${event.eventType}: основа ${String(event.plusCount)}, замена ${String(event.reserveCount)}`}
              </title>
              <rect
                x={x}
                y={36 - mainHeight}
                width={barWidth}
                height={mainHeight}
                rx="1"
                fill="url(#ak-bar)"
              />
              <rect
                x={x}
                y={36 - mainHeight - reserveHeight}
                width={barWidth}
                height={reserveHeight}
                rx="1"
                fill="var(--accent)"
                opacity="0.3"
              />
            </g>
          );
        })}

        <line x1="0" x2="100" y1="36" y2="36" stroke="var(--border-strong)" strokeWidth="0.3" />
      </svg>

      <div className="mt-3 flex items-center justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
        <span>{formatDateTime(data[0]?.startsAt ?? null, timezone)}</span>
        <span className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm" style={{ background: 'var(--accent)' }} />
            основа
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm" style={{ background: 'var(--accent)', opacity: 0.35 }} />
            замена
          </span>
        </span>
        <span>{formatDateTime(data.at(-1)?.startsAt ?? null, timezone)}</span>
      </div>
    </div>
  );
}

export function OverviewPage({ timezone }: { timezone: string }): ReactNode {
  const { data, loading, error, reload } = useResource<SummaryResponse>('/api/summary');
  const onLinkClick = useLinkHandler();

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  const upcoming = (data?.recentEvents ?? [])
    .filter((event) => event.status === 'open' && event.startsAt !== null)
    .sort((left, right) => (left.startsAt ?? '').localeCompare(right.startsAt ?? ''))
    .slice(0, 5);

  return (
    <>
      <PageHeader title="Обзор" description="Что происходит в сообществе прямо сейчас" />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {loading || data === null ? (
          <>
            <Panel className="h-28 animate-pulse" />
            <Panel className="h-28 animate-pulse" />
            <Panel className="h-28 animate-pulse" />
          </>
        ) : (
          <>
            <StatCard
              label="Игроков с анкетой"
              value={data.summary.profileCount}
              icon={<IconPlayers size={20} />}
              tone="accent"
            />
            <WinrateCard wins={data.summary.winCount} losses={data.summary.lossCount} />
            <StatCard
              label="Заявок на рассмотрении"
              value={data.summary.pendingApplicationCount}
              icon={<IconApplications size={20} />}
              tone={data.summary.pendingApplicationCount > 0 ? 'warning' : 'neutral'}
            />
          </>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <Panel>
          <PanelHeader
            title="Посещаемость"
            description="Последние мероприятия: сколько человек вышло в основе и осталось в замене"
          />
          {loading || data === null ? <Skeleton rows={5} /> : <AttendanceChart events={data.recentEvents} timezone={timezone} />}
        </Panel>

        <Panel>
          <PanelHeader title="Ближайшие наборы" />

          {loading || data === null ? (
            <Skeleton rows={3} />
          ) : upcoming.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              Открытых наборов нет.
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
              {upcoming.map((event) => (
                <li key={event.id}>
                  <a
                    href={`/events/${String(event.id)}`}
                    onClick={onLinkClick}
                    className="flex items-center justify-between gap-3 px-5 py-3.5 transition-colors hover:bg-[var(--surface-hover)]"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{event.eventType}</span>
                        <Badge tone={STATUS_TONE[event.status]}>{STATUS_LABEL[event.status]}</Badge>
                      </div>
                      <div className="mt-0.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                        {formatDateTime(event.startsAt, timezone)} · {formatRelative(event.startsAt)}
                      </div>
                    </div>
                    <div className="shrink-0 text-right tabular-nums">
                      <div className="text-sm font-medium">
                        {event.plusCount}/{event.capacity}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        +{event.reserveCount} в замене
                      </div>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
